import { Button, Input } from '@affine/component';
import { useWorkspaceCredentials } from '@affine/core/desktop/dialogs/setting/workspace-setting/credentials/use-workspace-credentials';
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';
import {
  FEATURE_KEY_METRICOOL,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { useI18n } from '@affine/i18n';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { connectionApi } from './connection-api';
import type {
  ConnectionServiceDefinition,
  WorkspaceConnectionSummary,
} from './types';

export interface ConnectServiceDialogProps {
  readonly workspaceId: string;
  readonly service: ConnectionServiceDefinition;
  readonly open: boolean;
  readonly readOnly?: boolean;
  readonly onConnected: (connection: WorkspaceConnectionSummary) => void;
  readonly onClose: () => void;
}

export function ConnectServiceDialog(props: ConnectServiceDialogProps) {
  return (
    <ConnectServiceDialogForm
      key={`${props.workspaceId}:${props.service.id}`}
      {...props}
    />
  );
}

function ConnectServiceDialogForm({
  workspaceId,
  service,
  open,
  readOnly,
  onConnected,
  onClose,
}: ConnectServiceDialogProps) {
  const t = useI18n();
  const [label, setLabel] = useState(service.name);
  const [credentialLabel, setCredentialLabel] = useState(
    `${service.name} credential`
  );
  const [credentialMode, setCredentialMode] = useState<'existing' | 'create'>(
    'create'
  );
  const [credentialId, setCredentialId] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const firstInput = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' || navigator.onLine
  );
  const { credentials } = useWorkspaceCredentials();
  const compatibleCredentials = credentials.filter(
    credential =>
      credential.service === service.slug &&
      ['Owner', 'Admin', 'Editor'].includes(credential.effectiveRole ?? '') &&
      service.connectionDefinition.credentialTypes.includes(credential.type)
  );

  const reset = () => {
    setLabel(service.name);
    setCredentialLabel(`${service.name} credential`);
    setCredentialMode('create');
    setCredentialId('');
    setFields({});
    setError(undefined);
    setSubmitting(false);
  };

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    firstInput.current?.focus();
    return () => restoreFocusRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (
      open &&
      compatibleCredentials.length > 0 &&
      service.connectionDefinition.setupFields.length === 0
    ) {
      setCredentialMode('existing');
    }
  }, [
    compatibleCredentials.length,
    open,
    service.connectionDefinition.setupFields.length,
  ]);

  const valid = useMemo(
    () =>
      label.trim().length > 0 &&
      (credentialMode === 'existing'
        ? credentialId.length > 0
        : credentialLabel.trim().length > 0 &&
          service.connectionDefinition.setupFields.every(
            field => !field.required || Boolean(fields[field.name]?.trim())
          )),
    [
      credentialId,
      credentialLabel,
      credentialMode,
      fields,
      label,
      service.connectionDefinition.setupFields,
    ]
  );

  if (!open) return null;

  const cancel = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const onDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled])'
      ) ?? []
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const submit = async () => {
    if (submitting || !valid || readOnly || !online) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const connection = await connectionApi.create(workspaceId, {
        serviceDefinitionId: service.id,
        label: label.trim(),
        credential:
          credentialMode === 'existing'
            ? { kind: 'existing', credentialId }
            : {
                kind: 'create',
                label: credentialLabel.trim(),
                fields,
              },
      });
      setFields({});
      onConnected(connection);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : t['com.streamlain.connections.operation-failed']()
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-service-title"
      onKeyDown={onDialogKeyDown}
    >
      <h2 id="connect-service-title">
        {t['com.streamlain.connections.connect-title']({
          service: service.name,
        })}
      </h2>
      {!online ? (
        <p role="status">{t['com.streamlain.connections.offline']()}</p>
      ) : null}
      {readOnly ? (
        <p role="status">
          {t['com.streamlain.connections.no-manage-permission']()}
        </p>
      ) : null}
      <label>
        {t['com.streamlain.connections.connection-name']()}
        <Input
          ref={firstInput}
          value={label}
          onChange={setLabel}
          disabled={submitting || readOnly || !online}
        />
      </label>
      {compatibleCredentials.length > 0 ? (
        <fieldset disabled={submitting || readOnly || !online}>
          <legend>{t['com.streamlain.connections.credential']()}</legend>
          <label>
            <input
              type="radio"
              checked={credentialMode === 'existing'}
              onChange={() => setCredentialMode('existing')}
            />
            {t['com.streamlain.connections.use-existing']()}
          </label>
          <label>
            <input
              type="radio"
              checked={credentialMode === 'create'}
              onChange={() => setCredentialMode('create')}
            />
            {t['com.streamlain.connections.create-new']()}
          </label>
        </fieldset>
      ) : null}
      {credentialMode === 'existing' ? (
        <label>
          {t['com.streamlain.connections.existing-credential']()}
          <select
            value={credentialId}
            disabled={submitting || readOnly || !online}
            onChange={event => setCredentialId(event.target.value)}
          >
            <option value="">
              {t['com.streamlain.connections.select-credential']()}
            </option>
            {compatibleCredentials.map(credential => (
              <option key={credential.id} value={credential.id}>
                {credential.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <label>
          {t['com.streamlain.connections.credential-name']()}
          <Input
            value={credentialLabel}
            onChange={setCredentialLabel}
            disabled={submitting || readOnly || !online}
          />
        </label>
      )}
      {credentialMode === 'create'
        ? service.connectionDefinition.setupFields.map(field => (
            <label key={field.name}>
              {field.label}
              <input
                type={field.secret ? 'password' : 'text'}
                autoComplete="off"
                value={fields[field.name] ?? ''}
                required={field.required}
                disabled={submitting || readOnly || !online}
                onChange={event =>
                  setFields(current => ({
                    ...current,
                    [field.name]: event.target.value,
                  }))
                }
              />
            </label>
          ))
        : null}
      {error ? <p role="alert">{error}</p> : null}
      <Button variant="secondary" onClick={cancel} disabled={submitting}>
        {t['com.streamlain.streamlain.common.cancel']()}
      </Button>
      <Button
        variant="primary"
        onClick={() => void submit()}
        disabled={!valid || submitting || readOnly || !online}
      >
        {submitting
          ? t['com.streamlain.connections.connecting']()
          : t['com.streamlain.connections.connect']()}
      </Button>
    </div>
  );
}

export function useConnectionServices(workspaceId: string) {
  const t = useI18n();
  const { isFeatureAllowed, isLoading: featureGateLoading } = useFeatureGate();
  const [services, setServices] = useState<
    readonly ConnectionServiceDefinition[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const generation = useRef(0);
  const reload = useCallback(async () => {
    const current = ++generation.current;
    setServices([]);
    setLoading(true);
    setError(undefined);
    try {
      const response = await defaultWorkspaceApiClient.request({
        workspaceId,
        path: '/api/service-definitions',
        category: 'settings',
        key: 'connections:service-definitions',
        mode: 'background',
        priority: 'visible',
      });
      if (!response.ok) {
        throw new Error(t['com.streamlain.connections.catalog-failed']());
      }
      const rows = (await response.json()) as ConnectionServiceDefinition[];
      if (generation.current === current) {
        setServices(
          rows.filter(
            row =>
              row.connectionDefinition.mode !== 'none' &&
              (row.slug !== 'metricool' ||
                isFeatureAllowed(FEATURE_KEY_METRICOOL))
          )
        );
      }
    } catch (cause) {
      if (generation.current !== current) return;
      setError(
        cause instanceof Error
          ? cause.message
          : t['com.streamlain.connections.catalog-failed']()
      );
    } finally {
      if (generation.current === current) setLoading(false);
    }
  }, [isFeatureAllowed, t, workspaceId]);
  useEffect(() => {
    void reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);
  return {
    services,
    loading: loading || featureGateLoading,
    error,
    reload,
  };
}
